<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import SearchAPI from 'dashboard/api/search';
import MessageApi from 'dashboard/api/inbox/message';
import {
  createContactSearcher,
  fetchContactableInboxes,
} from 'dashboard/components-next/NewConversation/helpers/composeConversationHelper';
import NextButton from 'dashboard/components-next/button/Button.vue';
import {
  MAX_FORWARD_DESTINATIONS,
  resolveDefaultInboxId,
  buildDestinationsPayload,
  hasUnresolvedInbox,
} from './forwardHelper';

const props = defineProps({
  // payload do context menu: { id, conversation_id, inbox_id, content, ... }
  message: { type: Object, required: true },
});

const emit = defineEmits(['close']);

const { t } = useI18n();

const query = ref('');
const isSearching = ref(false);
const isForwarding = ref(false);
const conversationResults = ref([]);
const contactResults = ref([]);
const selectedItems = ref([]);

const searchContacts = createContactSearcher();
let debounceTimer = null;
let searchSequence = 0;

const limitReached = computed(
  () => selectedItems.value.length >= MAX_FORWARD_DESTINATIONS
);
const canSubmit = computed(
  () =>
    selectedItems.value.length > 0 &&
    !hasUnresolvedInbox(selectedItems.value) &&
    !isForwarding.value
);

const isSelected = (type, id) =>
  selectedItems.value.some(item => item.type === type && item.id === id);

const conversationLabel = conversation => {
  const name =
    conversation.contact?.name || t('CONVERSATION.FORWARD_MODAL.NO_NAME');
  const inboxName = conversation.inbox?.name || '';
  return `#${conversation.id} · ${name}${inboxName ? ` · ${inboxName}` : ''}`;
};

const contactLabel = contact => {
  const reach = contact.phoneNumber || contact.email || '';
  return reach ? `${contact.name} · ${reach}` : contact.name;
};

const runSearch = async term => {
  searchSequence += 1;
  const sequence = searchSequence;
  isSearching.value = true;
  try {
    const [conversationsResponse, contacts] = await Promise.all([
      SearchAPI.conversations({ q: term }).catch(() => null),
      searchContacts(term).catch(() => []),
    ]);
    if (sequence !== searchSequence) return;
    conversationResults.value =
      conversationsResponse?.data?.payload?.conversations || [];
    // null = busca abortada (outra em andamento); mantém a lista anterior
    if (contacts !== null) contactResults.value = contacts || [];
  } finally {
    if (sequence === searchSequence) isSearching.value = false;
  }
};

watch(query, term => {
  clearTimeout(debounceTimer);
  const trimmed = term.trim();
  if (trimmed.length < 2) {
    conversationResults.value = [];
    contactResults.value = [];
    return;
  }
  debounceTimer = setTimeout(() => runSearch(trimmed), 300);
});

const selectConversation = conversation => {
  if (limitReached.value || isSelected('conversation', conversation.id)) return;
  selectedItems.value.push({
    type: 'conversation',
    id: conversation.id,
    label: conversationLabel(conversation),
  });
};

function removeItem(target) {
  selectedItems.value = selectedItems.value.filter(
    item => !(item.type === target.type && item.id === target.id)
  );
}

const selectContact = async contact => {
  if (limitReached.value || isSelected('contact', contact.id)) return;
  selectedItems.value.push({
    type: 'contact',
    id: contact.id,
    label: contactLabel(contact),
    inboxId: null,
    inboxOptions: [],
    loadingInboxes: true,
  });
  // proxy reativo do item recém-inserido (mutações precisam passar por ele)
  const item = selectedItems.value[selectedItems.value.length - 1];
  try {
    const inboxes = await fetchContactableInboxes(contact.id);
    item.inboxOptions = inboxes;
    item.inboxId = resolveDefaultInboxId(inboxes, props.message.inbox_id);
    if (!inboxes.length) {
      useAlert(t('CONVERSATION.FORWARD_MODAL.NO_INBOX'));
      removeItem(item);
    }
  } catch (error) {
    useAlert(t('CONVERSATION.FORWARD_MODAL.ERROR'));
    removeItem(item);
  } finally {
    item.loadingInboxes = false;
  }
};

const forwardMessage = async () => {
  if (!canSubmit.value) return;
  isForwarding.value = true;
  try {
    const { data } = await MessageApi.forward(
      props.message.conversation_id,
      props.message.id,
      buildDestinationsPayload(selectedItems.value)
    );
    const results = data.results || [];
    const failed = results.filter(result => !result.success).length;
    if (failed === 0) {
      useAlert(t('CONVERSATION.FORWARD_MODAL.SUCCESS'));
      emit('close');
    } else if (failed < results.length) {
      useAlert(
        t('CONVERSATION.FORWARD_MODAL.PARTIAL_ERROR', { count: failed })
      );
      emit('close');
    } else {
      useAlert(t('CONVERSATION.FORWARD_MODAL.ERROR'));
    }
  } catch (error) {
    useAlert(t('CONVERSATION.FORWARD_MODAL.ERROR'));
  } finally {
    isForwarding.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col w-full h-auto overflow-auto">
    <woot-modal-header
      :header-title="t('CONVERSATION.FORWARD_MODAL.TITLE')"
      :header-content="t('CONVERSATION.FORWARD_MODAL.DESCRIPTION')"
    />
    <div class="flex flex-col gap-3 px-8 pt-2 pb-6">
      <!-- selecionados -->
      <div v-if="selectedItems.length" class="flex flex-col gap-1.5">
        <div
          v-for="item in selectedItems"
          :key="`${item.type}-${item.id}`"
          class="flex items-center gap-2 px-2 py-1 rounded-lg bg-n-alpha-2 text-sm text-n-slate-12"
        >
          <span
            :class="
              item.type === 'conversation'
                ? 'i-lucide-messages-square'
                : 'i-lucide-user'
            "
            class="size-4 shrink-0 text-n-slate-11"
          />
          <span class="truncate flex-1">{{ item.label }}</span>
          <select
            v-if="item.type === 'contact' && item.inboxOptions.length > 1"
            v-model="item.inboxId"
            class="h-7 text-xs rounded outline outline-1 -outline-offset-1 outline-n-weak bg-n-alpha-black2 max-w-[40%]"
          >
            <option :value="null" disabled>
              {{ t('CONVERSATION.FORWARD_MODAL.SELECT_INBOX') }}
            </option>
            <option
              v-for="inbox in item.inboxOptions"
              :key="inbox.id"
              :value="inbox.id"
            >
              {{ inbox.name }}
            </option>
          </select>
          <span v-else-if="item.loadingInboxes" class="text-xs text-n-slate-10">
            ...
          </span>
          <NextButton
            ghost
            slate
            xs
            icon="i-lucide-x"
            @click="removeItem(item)"
          />
        </div>
        <span v-if="limitReached" class="text-xs text-amber-600">
          {{ t('CONVERSATION.FORWARD_MODAL.LIMIT_REACHED') }}
        </span>
      </div>

      <!-- busca -->
      <input
        v-model="query"
        :placeholder="t('CONVERSATION.FORWARD_MODAL.SEARCH_PLACEHOLDER')"
        class="h-10 px-3 rounded-lg outline outline-1 -outline-offset-1 outline-n-weak bg-n-alpha-black2 text-sm text-n-slate-12 focus:outline-n-brand transition-all"
      />

      <div
        v-if="query.trim().length >= 2"
        class="flex flex-col gap-1 max-h-64 overflow-y-auto"
      >
        <span v-if="isSearching" class="text-xs text-n-slate-10 px-1">
          {{ t('CONVERSATION.FORWARD_MODAL.SEARCHING') }}
        </span>
        <template v-if="conversationResults.length">
          <span class="text-xs font-medium text-n-slate-10 px-1 mt-1">
            {{ t('CONVERSATION.FORWARD_MODAL.CONVERSATIONS') }}
          </span>
          <button
            v-for="conversation in conversationResults"
            :key="`conv-${conversation.id}`"
            type="button"
            class="text-start px-2 py-1.5 rounded-lg text-sm text-n-slate-12 hover:bg-n-alpha-1 disabled:opacity-40"
            :disabled="
              limitReached || isSelected('conversation', conversation.id)
            "
            @click="selectConversation(conversation)"
          >
            {{ conversationLabel(conversation) }}
          </button>
        </template>
        <template v-if="contactResults.length">
          <span class="text-xs font-medium text-n-slate-10 px-1 mt-1">
            {{ t('CONVERSATION.FORWARD_MODAL.CONTACTS') }}
          </span>
          <button
            v-for="contact in contactResults"
            :key="`contact-${contact.id}`"
            type="button"
            class="text-start px-2 py-1.5 rounded-lg text-sm text-n-slate-12 hover:bg-n-alpha-1 disabled:opacity-40"
            :disabled="limitReached || isSelected('contact', contact.id)"
            @click="selectContact(contact)"
          >
            {{ contactLabel(contact) }}
          </button>
        </template>
        <span
          v-if="
            !isSearching &&
            !conversationResults.length &&
            !contactResults.length
          "
          class="text-xs text-n-slate-10 px-1"
        >
          {{ t('CONVERSATION.FORWARD_MODAL.NO_RESULTS') }}
        </span>
      </div>

      <div class="flex flex-row justify-end w-full gap-2 pt-2">
        <NextButton
          faded
          slate
          :label="t('CONVERSATION.FORWARD_MODAL.CANCEL')"
          @click="emit('close')"
        />
        <NextButton
          :label="t('CONVERSATION.FORWARD_MODAL.SUBMIT')"
          :disabled="!canSubmit"
          :is-loading="isForwarding"
          icon="i-lucide-share"
          @click="forwardMessage"
        />
      </div>
    </div>
  </div>
</template>
